import { Reducer, AnyAction } from 'redux';
import { action, createReducer } from 'typesafe-actions';
import BindingsDTO from '@/dtos/bindings/BindingsDTO';
import { PageDTO } from '@/dtos/generics';
import SearchQuery from '@/utils/SearchQuery';
import { genericError, genericRequest } from '../utils';
import BindingsState from './types';
import CreateBindingsDTO from '@/dtos/bindings/CreateBindingsDTO';
import BindingGenealogyDTO from '@/dtos/bindings/BindingGenealogyDTO';

const INITIAL_STATE: BindingsState = {
  data: [],
  next: undefined,
  filters: SearchQuery.build(),
  selected: undefined,
  currentPage: 1,
  prev: undefined,
  total: 0,
  error: false,
  loading: false,
  genealogy: [],
  loadingGenealogy: false,
  errorGenealogy: false,
};

export enum BindingsTypes {
  SET_LOADING = '@bindings/SET_LOADING',
  SET_FILTERS = '@bindings/SET_FILTERS',
  LIST_REQUEST = '@bindings/LIST_REQUEST',
  LIST_SUCCESS = '@bindings/LIST_SUCCESS',
  LIST_ERROR = '@bindings/LIST_ERROR',
  GET_REQUEST = '@bindings/GET_REQUEST',
  GET_SUCCESS = '@bindings/GET_SUCCESS',
  GET_ERROR = '@bindings/GET_ERROR',
  CREATE_REQUEST = '@bindings/CREATE_REQUEST',
  CREATE_SUCCESS = '@bindings/CREATE_SUCCESS',
  CREATE_ERROR = '@bindings/CREATE_ERROR',
  CLEAR = '@bindings/CLEAR',
  LOAD_TRACE_REQUEST = '@bindings/LOAD_TRACE_REQUEST',
  LOAD_TRACE_SUCCESS = '@bindings/LOAD_TRACE_SUCCESS',
  LOAD_TRACE_FAILURE = '@bindings/LOAD_TRACE_FAILURE',
  CLEAR_GENEALOGY = '@bindings/CLEAR_GENEALOGY',
}

const BindingsActions = {
  setLoading: (loading: boolean) =>
    action(BindingsTypes.SET_LOADING, { loading }),
  setFilters: (filters: SearchQuery) =>
    action(BindingsTypes.SET_FILTERS, { filters }),

  listRequest: (filters: SearchQuery, page?: number) =>
    action(BindingsTypes.LIST_REQUEST, { filters, page }),
  listSuccess: (page: PageDTO<BindingsDTO>) =>
    action(BindingsTypes.LIST_SUCCESS, page),
  listError: (error: string) => action(BindingsTypes.LIST_ERROR, { error }),

  getRequest: (id: string) => action(BindingsTypes.GET_REQUEST, id),
  getSuccess: (resource: BindingsDTO) =>
    action(BindingsTypes.GET_SUCCESS, resource),
  getError: (error: string) => action(BindingsTypes.GET_ERROR, { error }),

  createRequest: (data: CreateBindingsDTO) =>
    action(BindingsTypes.CREATE_REQUEST, data),
  createSuccess: (resource: BindingsDTO) =>
    action(BindingsTypes.CREATE_SUCCESS, resource),
  createError: (error: string) => action(BindingsTypes.CREATE_ERROR, { error }),

  clear: () => action(BindingsTypes.CLEAR),

  loadTraceRequest: (code: string) =>
    action(BindingsTypes.LOAD_TRACE_REQUEST, { code }),
  loadTraceSuccess: (data: BindingGenealogyDTO[]) =>
    action(BindingsTypes.LOAD_TRACE_SUCCESS, { data }),
  loadTraceFailure: () => action(BindingsTypes.LOAD_TRACE_FAILURE),
  clearGenealogy: () => action(BindingsTypes.CLEAR_GENEALOGY),
};

export default BindingsActions;

type BindingsReducer<Action extends AnyAction> = Reducer<BindingsState, Action>;

const setLoading: BindingsReducer<
  ReturnType<typeof BindingsActions.setLoading>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  loading: payload.loading,
});

const setFilters: BindingsReducer<
  ReturnType<typeof BindingsActions.setFilters>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  filters: payload.filters,
});

const listSuccess: BindingsReducer<
  ReturnType<typeof BindingsActions.listSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  data: payload.data,
  currentPage: payload.currentPage,
  next: payload.next,
  prev: payload.prev,
  total: payload.total,
  loading: false,
});

const getSuccess: BindingsReducer<
  ReturnType<typeof BindingsActions.getSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  selected: payload,
  loading: false,
});

const createSuccess: BindingsReducer<
  ReturnType<typeof BindingsActions.createSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  data: [...state.data, payload],
  loading: false,
});

const clear: BindingsReducer<ReturnType<typeof BindingsActions.clear>> = () =>
  INITIAL_STATE;

const loadTraceRequest: BindingsReducer<AnyAction> = (state = INITIAL_STATE) => ({
  ...state,
  loadingGenealogy: true,
  errorGenealogy: false,
  genealogy: [],
});

const loadTraceSuccess: BindingsReducer<
  ReturnType<typeof BindingsActions.loadTraceSuccess>
> = (state = INITIAL_STATE, { payload }) => ({
  ...state,
  loadingGenealogy: false,
  genealogy: payload.data,
});

const loadTraceFailure: BindingsReducer<AnyAction> = (state = INITIAL_STATE) => ({
  ...state,
  loadingGenealogy: false,
  errorGenealogy: true,
});

const clearGenealogy: BindingsReducer<AnyAction> = (state = INITIAL_STATE) => ({
  ...state,
  genealogy: [],
  loadingGenealogy: false,
  errorGenealogy: false,
});

export const reducer = createReducer<BindingsState>(INITIAL_STATE)
  .handleAction(BindingsTypes.SET_LOADING, setLoading)
  .handleAction(BindingsTypes.SET_FILTERS, setFilters)
  .handleAction(BindingsTypes.LIST_REQUEST, genericRequest)
  .handleAction(BindingsTypes.LIST_SUCCESS, listSuccess)
  .handleAction(BindingsTypes.LIST_ERROR, genericError)
  .handleAction(BindingsTypes.GET_REQUEST, genericRequest)
  .handleAction(BindingsTypes.GET_SUCCESS, getSuccess)
  .handleAction(BindingsTypes.GET_ERROR, genericError)
  .handleAction(BindingsTypes.CREATE_REQUEST, genericRequest)
  .handleAction(BindingsTypes.CREATE_SUCCESS, createSuccess)
  .handleAction(BindingsTypes.CREATE_ERROR, genericError)
  .handleAction(BindingsTypes.CLEAR, clear)
  .handleAction(BindingsTypes.LOAD_TRACE_REQUEST, loadTraceRequest)
  .handleAction(BindingsTypes.LOAD_TRACE_SUCCESS, loadTraceSuccess)
  .handleAction(BindingsTypes.LOAD_TRACE_FAILURE, loadTraceFailure)
  .handleAction(BindingsTypes.CLEAR_GENEALOGY, clearGenealogy);